import { supabase } from '../config/supabase';
import { Database } from '../config/supabase';

export type CompanyStats = Database['public']['Views']['company_stats']['Row'];

export interface CompanyReview {
  id: string;
  company_id: string;
  user_id: string;
  overall_rating: number;
  culture_rating: number;
  salary_rating: number;
  growth_rating: number;
  work_life_rating: number;
  comment?: string | null;
  job_position?: string | null;
  time_at_company?: string | null;
  would_recommend: boolean;
  review_date: string;
  is_approved: boolean;
  is_flagged: boolean;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  user?: {
    email?: string;
  };
  company?: {
    company_name?: string;
    company_slug?: string;
  };
}

export interface CompanyFilters {
  search?: string;
  minRating?: number;
  minReviews?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface ReviewFilters {
  companyId?: string;
  isApproved?: boolean;
  minRating?: number;
  userId?: string;
}

export interface PaginationParams {
  page: number;
  limit: number;
}

export interface CompaniesResponse {
  companies: CompanyStats[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ReviewsResponse {
  reviews: CompanyReview[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Serviço para gerenciar empresas e reviews
 */
class CompaniesService {
  /**
   * Busca empresas com paginação e filtros
   */
  async getCompanies(
    filters: CompanyFilters = {},
    pagination: PaginationParams = { page: 1, limit: 20 }
  ): Promise<CompaniesResponse> {
    try {
      let query = supabase.from('company_stats').select('*', { count: 'exact' });

      // Aplicar filtros
      if (filters.search) {
        query = query.or(`company_name.ilike.%${filters.search}%,company_slug.ilike.%${filters.search}%`);
      }

      if (filters.minRating !== undefined) {
        query = query.gte('avg_overall_rating', filters.minRating);
      }

      if (filters.minReviews !== undefined) {
        query = query.gte('total_reviews', filters.minReviews);
      }

      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }

      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      // Aplicar paginação
      const offset = (pagination.page - 1) * pagination.limit;
      query = query.range(offset, offset + pagination.limit - 1);

      // Ordenar por número de reviews (mais reviews primeiro)
      query = query.order('total_reviews', { ascending: false });

      const { data, error, count } = await query;

      if (error) {
        console.error('Erro ao buscar empresas:', error);
        throw error;
      }

      const total = count || 0;
      const totalPages = Math.ceil(total / pagination.limit);

      return {
        companies: (data || []) as CompanyStats[],
        total,
        page: pagination.page,
        limit: pagination.limit,
        totalPages,
      };
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
      throw error;
    }
  }

  /**
   * Busca detalhes de uma empresa específica
   */
  async getCompanyById(companyId: string): Promise<CompanyStats | null> {
    try {
      const { data, error } = await supabase
        .from('company_stats')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        console.error('Erro ao buscar empresa:', error);
        throw error;
      }

      return data as CompanyStats;
    } catch (error) {
      console.error('Erro ao buscar empresa:', error);
      return null;
    }
  }

  /**
   * Busca reviews de uma empresa
   */
  async getCompanyReviews(
    companyId: string,
    filters: ReviewFilters = {},
    pagination: PaginationParams = { page: 1, limit: 20 }
  ): Promise<ReviewsResponse> {
    try {
      let query = supabase
        .from('company_reviews')
        .select('*, linkedin_companies(company_name, company_slug)', { count: 'exact' })
        .eq('company_id', companyId)
        .is('deleted_at', null);

      // Aplicar filtros
      if (filters.isApproved !== undefined) {
        query = query.eq('is_approved', filters.isApproved);
      }

      if (filters.minRating !== undefined) {
        query = query.gte('overall_rating', filters.minRating);
      }

      if (filters.userId) {
        query = query.eq('user_id', filters.userId);
      }

      // Aplicar paginação
      const offset = (pagination.page - 1) * pagination.limit;
      query = query.range(offset, offset + pagination.limit - 1);

      // Ordenar por data (mais recente primeiro)
      query = query.order('created_at', { ascending: false });

      const { data, error, count } = await query;

      if (error) {
        console.error('Erro ao buscar reviews:', error);
        throw error;
      }

      // Processar reviews e buscar emails dos usuários
      const reviews = (data || []) as any[];
      const userIds = [...new Set(reviews.map((r) => r.user_id))];

      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('extension_user_stats')
          .select('user_id, email')
          .in('user_id', userIds);

        const userMap = new Map(users?.map((u) => [u.user_id, u.email]) || []);

        reviews.forEach((review: any) => {
          review.user = {
            email: userMap.get(review.user_id) || 'N/A',
          };
          // Processar relacionamento com empresa
          if (review.linkedin_companies) {
            if (Array.isArray(review.linkedin_companies) && review.linkedin_companies.length > 0) {
              review.company = review.linkedin_companies[0];
            } else if (typeof review.linkedin_companies === 'object') {
              review.company = review.linkedin_companies;
            }
            delete review.linkedin_companies;
          }
        });
      }

      const processedReviews = reviews as CompanyReview[];

      const total = count || 0;
      const totalPages = Math.ceil(total / pagination.limit);

      return {
        reviews: processedReviews,
        total,
        page: pagination.page,
        limit: pagination.limit,
        totalPages,
      };
    } catch (error) {
      console.error('Erro ao buscar reviews:', error);
      throw error;
    }
  }

  /**
   * Busca todas as reviews pendentes de aprovação
   */
  async getPendingReviews(pagination: PaginationParams = { page: 1, limit: 20 }): Promise<ReviewsResponse> {
    return this.getAllReviews({ isApproved: false }, pagination);
  }

  /**
   * Busca todas as reviews com filtros
   */
  async getAllReviews(
    filters: ReviewFilters = {},
    pagination: PaginationParams = { page: 1, limit: 20 }
  ): Promise<ReviewsResponse> {
    try {
      let query = supabase
        .from('company_reviews')
        .select('*, linkedin_companies(company_name, company_slug)', { count: 'exact' })
        .is('deleted_at', null);

      // Aplicar filtros
      if (filters.companyId) {
        query = query.eq('company_id', filters.companyId);
      }

      if (filters.isApproved !== undefined) {
        query = query.eq('is_approved', filters.isApproved);
      }

      if (filters.minRating !== undefined) {
        query = query.gte('overall_rating', filters.minRating);
      }

      if (filters.userId) {
        query = query.eq('user_id', filters.userId);
      }

      // Aplicar paginação
      const offset = (pagination.page - 1) * pagination.limit;
      query = query.range(offset, offset + pagination.limit - 1);

      // Ordenar por data (mais recente primeiro)
      query = query.order('created_at', { ascending: false });

      const { data, error, count } = await query;

      if (error) {
        console.error('Erro ao buscar reviews:', error);
        throw error;
      }

      const reviews = (data || []) as any[];
      const userIds = [...new Set(reviews.map((r) => r.user_id))];

      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('extension_user_stats')
          .select('user_id, email')
          .in('user_id', userIds);

        const userMap = new Map(users?.map((u) => [u.user_id, u.email]) || []);

        reviews.forEach((review: any) => {
          review.user = {
            email: userMap.get(review.user_id) || 'N/A',
          };
          // Processar relacionamento com empresa
          if (review.linkedin_companies) {
            if (Array.isArray(review.linkedin_companies) && review.linkedin_companies.length > 0) {
              review.company = review.linkedin_companies[0];
            } else if (typeof review.linkedin_companies === 'object') {
              review.company = review.linkedin_companies;
            }
            delete review.linkedin_companies;
          }
        });
      }

      const processedReviews = reviews as CompanyReview[];
      const total = count || 0;
      const totalPages = Math.ceil(total / pagination.limit);

      return {
        reviews: processedReviews,
        total,
        page: pagination.page,
        limit: pagination.limit,
        totalPages,
      };
    } catch (error) {
      console.error('Erro ao buscar reviews:', error);
      throw error;
    }
  }

  /**
   * Aprova uma review
   */
  async approveReview(reviewId: string, adminId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('company_reviews')
        .update({ is_approved: true, updated_at: new Date().toISOString() })
        .eq('id', reviewId);

      if (error) {
        console.error('Erro ao aprovar review:', error);
        return false;
      }

      // Registrar log
      try {
        await supabase.rpc('log_admin_action', {
          p_admin_id: adminId,
          p_action: 'review_approved',
          p_details: { review_id: reviewId },
          p_severity: 'info',
          p_resource_type: 'company_review',
          p_resource_id: reviewId,
        });
      } catch (logError) {
        console.warn('Erro ao registrar log:', logError);
      }

      return true;
    } catch (error) {
      console.error('Erro ao aprovar review:', error);
      return false;
    }
  }

  /**
   * Reprova uma review
   */
  async rejectReview(reviewId: string, adminId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('company_reviews')
        .update({ is_approved: false, updated_at: new Date().toISOString() })
        .eq('id', reviewId);

      if (error) {
        console.error('Erro ao reprovar review:', error);
        return false;
      }

      // Registrar log
      try {
        await supabase.rpc('log_admin_action', {
          p_admin_id: adminId,
          p_action: 'review_rejected',
          p_details: { review_id: reviewId },
          p_severity: 'info',
          p_resource_type: 'company_review',
          p_resource_id: reviewId,
        });
      } catch (logError) {
        console.warn('Erro ao registrar log:', logError);
      }

      return true;
    } catch (error) {
      console.error('Erro ao reprovar review:', error);
      return false;
    }
  }

  /**
   * Deleta uma review (soft delete)
   */
  async deleteReview(reviewId: string, adminId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('company_reviews')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', reviewId);

      if (error) {
        console.error('Erro ao deletar review:', error);
        return false;
      }

      // Registrar log
      try {
        await supabase.rpc('log_admin_action', {
          p_admin_id: adminId,
          p_action: 'review_deleted',
          p_details: { review_id: reviewId },
          p_severity: 'warning',
          p_resource_type: 'company_review',
          p_resource_id: reviewId,
        });
      } catch (logError) {
        console.warn('Erro ao registrar log:', logError);
      }

      return true;
    } catch (error) {
      console.error('Erro ao deletar review:', error);
      return false;
    }
  }

  /**
   * Exporta lista de empresas
   */
  async exportCompanies(filters: CompanyFilters = {}): Promise<CompanyStats[]> {
    try {
      let query = supabase.from('company_stats').select('*');

      if (filters.search) {
        query = query.or(`company_name.ilike.%${filters.search}%,company_slug.ilike.%${filters.search}%`);
      }

      if (filters.minRating !== undefined) {
        query = query.gte('avg_overall_rating', filters.minRating);
      }

      if (filters.minReviews !== undefined) {
        query = query.gte('total_reviews', filters.minReviews);
      }

      query = query.order('total_reviews', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao exportar empresas:', error);
        throw error;
      }

      return (data || []) as CompanyStats[];
    } catch (error) {
      console.error('Erro ao exportar empresas:', error);
      throw error;
    }
  }
}

export default new CompaniesService();

